import React, { useMemo, useCallback, Suspense, lazy, useRef, useState, useEffect } from 'react';
import { Modal, ProgressBar } from 'is24-corecss';
import { css } from '@emotion/css';
import type { StepComponentRef } from './types';
import { useBillCheckV3 } from '../../hooks/useBillCheckV3.hook';
import LoadingSkeleton from './LoadingSkeleton';
import { useIntl } from '@/i18n/hooks/useIntl';
import { useMFENavigation } from '@/customHooks/useMFENavigation.hook';
import {
  ACCOUNTING_PERIOD_DETAILS,
  BALANCE_DETAILS,
  CUSTOMER_DETAILS,
  ERROR,
  NEBENKOSTEN_CHECK,
  OPERATIONAL_STATEMENT_UPLOAD,
  RENTAL_CONTRACT_UPLOAD,
} from '@/domain/routes';
import { BILLCHECK_V3_STATUS } from '@/@types/billCheckV3';
import { useGetBillCheckDetails } from '@/apiHooks/billCheckV3.hook';
import { useGetProductsStatus } from '@/apiHooks/productsStatus.hook';
import { TrackingEvents } from '@/tracking/trackingEvents';
import { report } from '@/tracking/tracking';
import { useModalFocus } from '@/customHooks/useModalFocus.hook';

// Lazy load components
const PersonalDetailsStep = lazy(() => import('./PersonalDetailsStep'));
const AccountingPeriodStep = lazy(() => import('./AccountingPeriodStep'));
const BalanceDetailsStep = lazy(() => import('./BalanceDetailsStep'));
const DocumentUploadStep = lazy(() => import('./DocumentUploadStep'));

export const BILLCHECK_FLOW_STEPS = [
  CUSTOMER_DETAILS,
  BALANCE_DETAILS,
  ACCOUNTING_PERIOD_DETAILS,
  OPERATIONAL_STATEMENT_UPLOAD,
  RENTAL_CONTRACT_UPLOAD,
  ERROR,
] as const;

const styles = {
  progressBar: css`
    .ProgressBar_progress-bar-filled__saO-H {
      background-color: var(--color-background-button-default-default) !important;
    }
  `,
  modalBackdrop: css`
    > div > [class*='Modal_modal-title'],
    > div > [class*='Modal_modal-footer'] {
      border: 0;
    }
  `,
  modalContent: css`
    min-height: 300px;
  `,
};

const STEPS = [
  {
    id: 'personal-details',
    component: PersonalDetailsStep,
    route: CUSTOMER_DETAILS,
    trackingEvent: TrackingEvents.BILLCHECK.CREATION_FLOW.CUSTOMER_DETAILS,
  },
  {
    id: 'balance',
    component: BalanceDetailsStep,
    route: BALANCE_DETAILS,
    trackingEvent: TrackingEvents.BILLCHECK.CREATION_FLOW.BALANCE_DETAILS,
  },
  {
    id: 'accounting-period',
    component: AccountingPeriodStep,
    route: ACCOUNTING_PERIOD_DETAILS,
    trackingEvent: TrackingEvents.BILLCHECK.CREATION_FLOW.ACCOUNTING_PERIOD_DETAILS,
  },
  {
    id: 'document-upload',
    component: DocumentUploadStep,
    route: OPERATIONAL_STATEMENT_UPLOAD,
    trackingEvent: TrackingEvents.BILLCHECK.CREATION_FLOW.OPERATIONAL_STATEMENT_UPLOAD,
  },
] as const;

const MultiStepCreationModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { t } = useIntl();
  // Add ref to communicate with current step
  const currentStepRef = useRef<StepComponentRef>(null);
  const { nestedModalPath, navigateToNestedPath, navigateToNestedModalPath } = useMFENavigation();
  const [isStepValid, setIsStepValid] = useState(false);
  const { areProductsRefetching } = useGetProductsStatus();
  const { lastBillCheck } = useBillCheckV3();
  const { isBillCheckDetailsSuccess, initializing, isBillCheckDetailsError } = useGetBillCheckDetails(
    lastBillCheck?.billCheckFlowId,
  );

  useModalFocus(open);

  // Memoize translated labels
  const labels = useMemo(
    () => ({
      step: t({
        id: 'benefits.billCheckV3.multiStepFlow.progressBar.step',
        defaultMessage: 'Schritt',
      }),
      submit: t({
        id: 'benefits.billCheckV3.multiStepFlow.rentalContractUploadStep.submitCta',
        defaultMessage: 'Submit',
      }),
      next: t({
        id: 'common.buttons.next',
        defaultMessage: 'Next',
      }),
      previous: t({
        id: 'common.buttons.previous',
        defaultMessage: 'Previous',
      }),
    }),
    [t],
  );

  useEffect(() => {
    if (
      !areProductsRefetching &&
      !initializing &&
      isBillCheckDetailsSuccess &&
      lastBillCheck?.billCheckStatus !== BILLCHECK_V3_STATUS.STARTED
    ) {
      navigateToNestedPath(NEBENKOSTEN_CHECK);
    }
    if (isBillCheckDetailsError && lastBillCheck?.billCheckFlowId) {
      navigateToNestedModalPath(ERROR);
    }
  }, [
    isBillCheckDetailsSuccess,
    areProductsRefetching,
    initializing,
    isBillCheckDetailsError,
    lastBillCheck?.billCheckFlowId,
  ]);

  // Memoize current step calculations
  const stepInfo = useMemo(() => {
    const currentStepIndex = STEPS.findIndex((step) => step.route === nestedModalPath);
    const validIndex = currentStepIndex !== -1 ? currentStepIndex : 0;
    const currentStep = STEPS[validIndex];

    return {
      currentStep,
      currentStepIndex: validIndex,
      isFirstStep: validIndex === 0,
      isLastStep: validIndex === STEPS.length - 1,
      progress: ((validIndex + 1) / STEPS.length) * 100,
      nextRoute: STEPS[validIndex + 1]?.route,
      prevRoute: STEPS[validIndex - 1]?.route,
      trackingEvent: currentStep.trackingEvent,
    };
  }, [nestedModalPath]);

  const { currentStep, currentStepIndex, isFirstStep, isLastStep, progress } = stepInfo;
  const StepComponent = currentStep.component;

  const isNextButtonDisabled = !isStepValid;

  // Updated navigation handlers
  const handleNext = useCallback(() => {
    // Trigger form submission in the current step component
    if (currentStepRef.current?.submitForm) {
      currentStepRef.current.submitForm();
    }
  }, [currentStepRef]); // Add currentStepRef to dependencies

  const handlePrevious = useCallback(() => {
    if (stepInfo.prevRoute) {
      report(stepInfo.trackingEvent, true);
      navigateToNestedModalPath(stepInfo.prevRoute);
    }
  }, [stepInfo.prevRoute, navigateToNestedModalPath, stepInfo.trackingEvent]);

  const handleStepComplete = useCallback(() => {
    if (stepInfo.nextRoute) {
      report(stepInfo.trackingEvent, true);
      navigateToNestedModalPath(stepInfo.nextRoute);
    }
  }, [stepInfo.nextRoute, navigateToNestedModalPath, stepInfo.trackingEvent]);

  return (
    <Modal.Root isOpen={open} onClose={onClose}>
      <Modal.Backdrop className={styles.modalBackdrop}>
        <Modal.Surface>
          <Modal.Header>
            <Modal.TitleGroup>
              <Modal.Title>
                {t({
                  id: 'benefits.billCheckV3.headline',
                  defaultMessage: 'Nebenkosten-Check',
                })}
              </Modal.Title>
            </Modal.TitleGroup>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <div className={`billCheck-modal-content display-flex flex-column gap-l ${css(styles.modalContent)}`}>
              <div className={styles.progressBar}>
                <div className='display-flex one-whole grid-justify-space-between'>
                  <span>{labels.step}</span>
                  <span>
                    {currentStepIndex + 1} / {STEPS.length}
                  </span>
                </div>
                <ProgressBar progress={progress} />
              </div>
              <Suspense fallback={<LoadingSkeleton />}>
                <StepComponent
                  ref={currentStepRef}
                  onNext={handleStepComplete}
                  onPrevious={handlePrevious}
                  isFirstStep={isFirstStep}
                  isLastStep={isLastStep}
                  onValidityChange={setIsStepValid}
                />
              </Suspense>
            </div>
          </Modal.Body>
          <Modal.Footer>
            {isFirstStep ? undefined : <Modal.SecondaryFooterButton label={labels.previous} onClick={handlePrevious} />}
            {
              <Modal.PrimaryFooterButton
                label={isLastStep ? labels.submit : labels.next}
                onClick={handleNext}
                disabled={isNextButtonDisabled}
              />
            }
          </Modal.Footer>
        </Modal.Surface>
      </Modal.Backdrop>
    </Modal.Root>
  );
};
export default MultiStepCreationModal;
